import React, { useState, useEffect, useRef } from "react";
import {
  Table,
  ScrollArea,
  rem,
  Text,
  UnstyledButton,
  Group,
  Center,
  Select,
  Button,
  Checkbox,
  Pagination,
} from "@mantine/core";
import {
  IconSelector,
  IconChevronDown,
  IconChevronUp,
  IconTrash,
  IconEdit,
  IconBrandTabler,
  IconEye,
} from "@tabler/icons-react";
import { showErrorAlert, showSuccessAlert } from "../../helper/alerts";
import DeleteModal from "./DeleteModal";
import AddReviewModal from "./AddReviewModal";
import EditReviewModal from "./EditReviewModal";
import classes from "../BannerPage/Banner.module.css";
import { RowDataReview } from "./types";
import { useFetchRowData } from "../hooks/useFetchRowData";
import { review } from "../types/RowDataTypes";
import { CustomTable } from "../Table/CustomTable";
import { SearchBar } from "../SearchBar/SearchBar";
import useAxiosPrivate from "../../auth/useAxiosPrivate";
import Header from "../static/Header";
// import getBannerTable from "../../DataDisplay/DataDisplayTable/BannerTable";
import getReviewTable from "../../DataDisplay/DataDisplayTable/ReviewTable";

// Searching and Sorting
enum ColumnHeaderToFilterPair {
  NAME = "Name",
  REVIEW = "Review",
  RATING = "Rating",
//   COLOR = "Color",
  SHOW = "Show",
}

const colFilter = ColumnHeaderToFilterPair;
const colFilters: { [key in ColumnHeaderToFilterPair]: string } = {
  [colFilter.NAME]: "fullname",
  [colFilter.REVIEW]: "review",
  [colFilter.RATING]: "rating",
//   [colFilter.COLOR]: "color",
  [colFilter.SHOW]: "show",
};

interface ThProps {
  children: React.ReactNode;
  sortable: boolean;
  reversed: boolean;
  sorted: boolean;
  onSort(): void;
}

function Th({ children, sortable, reversed, sorted, onSort }: ThProps) {
  const Icon = sorted
    ? reversed
      ? IconChevronUp
      : IconChevronDown
    : IconSelector;

  const allowedSorts = [
    colFilter.NAME.toLowerCase(),
    colFilter.SHOW.toLowerCase(),
  ];

  return (
    <Table.Th className={`${classes.th} ${sortable ? classes.sortable : ""}`}>
      {allowedSorts.includes(children!!.toString().toLowerCase()) ? (
        <UnstyledButton onClick={onSort} className={classes.control}>
          <Group justify="space-between">
            <Text className={classes.Text}>{children}</Text>
            <Center className={classes.icon}>
              <Icon style={{ width: rem(16), height: rem(16) }} stroke={1.5} />
            </Center>
          </Group>
        </UnstyledButton>
      ) : (
        <Text fw={500} fz="sm">
          {children}
        </Text>
      )}
    </Table.Th>
  );
}

const initialData: RowDataReview[] = [
  {
    id: "",
    fullname: "",
    review: "",
    rating: 0,
    reviewDate: "",
    show: false,
    action: "",
  },
];

export function Review() {
  const axiosPrivate = useAxiosPrivate();

  const [search, setSearch] = useState("");
  const [tableData, setTableData] = useState(initialData);
  const [sortBy, setSortBy] = useState<keyof RowDataReview | null>(null);
  const [reverseSortDirection, setReverseSortDirection] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  //Used in searching and when deleting to cause rerender to fetch data
  const [tableStateChanged, setTableStateChanged] = useState(0);

  const changeTableState = () => {
    setTableStateChanged(Math.random() * Math.random());
  };

  const [selectedFilters, setSelectedFilters] = useState<string[]>([]);
  function resetPage() {
    if (currentPage !== 1) {
      setCurrentPage(1);
      return true;
    }
    return false;
  }

  const checkboxesFilter: JSX.Element[] = [];

  //Set allowed search filter checkbox to be shown
  const allowedFilters = [
    colFilters[colFilter.NAME],
  ];
  for (const [key, value] of Object.entries(colFilters)) {
    if (allowedFilters.includes(value)) {
      const checkboxFilter: JSX.Element = (
        <Checkbox value={value} label={key} />
      );
      checkboxesFilter.push(checkboxFilter);
    }
  }

  const [showFilterOptions, setShowFilterOptions] = useState(false);

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.target.value;
    setSearch(input);
  };

  const toggleVisibility = async (row: RowDataReview, e: any) => {
    e.target.disabled = true;
    let endpoint;
    if (!row.show) endpoint = "/api/admin/review/show";
    else endpoint = "/api/admin/review/hide";

    await axiosPrivate
      .post(endpoint, {
        id: row.id,
      })
      .then((response) => {
        const indexOfSelectedRow = rowDataBanner!!.findIndex((e) => e === row);

        // Create a copy of the sortedData
        const updatedData = [...rowDataBanner!!];

        // Update the selectedRow in the copied data
        updatedData[indexOfSelectedRow] = {
          ...row,
          show: !row.show,
        };

        setRowDataBanner(updatedData);
      })
      .catch((err) => {
        showErrorAlert("An error occured");
      });

    e.target.disabled = false;
  };

  const fetchedData = useFetchRowData(
    itemsPerPage,
    currentPage,
    search,
    selectedFilters,
    tableStateChanged,
    sortBy ?? "createdAt",
    reverseSortDirection ? "DESC" : "ASC",
    review,
    getReviewTable().create(),
    "",
  );
  const itemsOnPage = useRef(0);
  const [totalRows, setTotalRows] = useState<number>(0);
  // const totalRows = fetchedData.totalRows;
  let rowData: RowDataReview[] | null = fetchedData.data as RowDataReview[];
  const isLoading: boolean = fetchedData.isLoading;
  const [rowDataBanner, setRowDataBanner] = useState<RowDataReview[] | null>(
    null
  );

  const [isAddBannerModalOpen, setAddBannerModalOpen] = useState(false);
  const [isEditBannerModalOpen, setEditBannerModalOpen] = useState(false);
  const [selectedRowForEdit, setSelectedRowForEdit] =
    useState<RowDataReview | null>(null);
  const [isViewBannerModalOpen, setViewBannerModalOpen] = useState(false);
  const [selectedRowForView, setSelectedRowForView] =
    useState<RowDataReview | null>(null);

  const [isDeleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [selectedRowToDelete, setSelectedRowToDelete] =
    useState<RowDataReview | null>(null);

  const handleAddBanner = () => {
    setAddBannerModalOpen(true);
  };

  const handleAddBannerModalClose = () => {
    setAddBannerModalOpen(false);
  };

  const handleEdit = (row: RowDataReview) => {
    setSelectedRowForEdit(row);
    setEditBannerModalOpen(true);
  };

  const handleAddBannerSuccess = (newData: RowDataReview) => {
    axiosPrivate
      .post(
        "/api/admin/review/add",
        {
            fullname: newData.fullname,
            review: newData.review,
            rating: newData.rating,
            reviewDate: newData.reviewDate,
        },
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      )
      .then((response) => {
        // console.log(response);

        if (itemsOnPage.current < itemsPerPage) {
          const data = response.data.links;
          const returnedData: RowDataReview = {
            ...newData,
            id: data.id,
            fullname: data.fullname,
            review: data.review,
            rating: data.rating,
            reviewDate: data.reviewDate,
          };
          if (!reverseSortDirection)
            setRowDataBanner((prevData) => [returnedData, ...prevData!!]);
          else setRowDataBanner((prevData) => [...prevData!!, returnedData]);
        }
        itemsOnPage.current = itemsOnPage.current + 1;
        setTotalRows((prevData) => prevData + 1);
        showSuccessAlert("Review Successfully Added");
      })
      .catch((error) => {
        // console.log("error " + error);
        showErrorAlert("Something Went Wrong");
      });
  };

  const handleEditBannerSuccess = (editedData: RowDataReview) => {

    let params: any = {
      id: editedData.id,
      fullname: editedData.fullname,
        review: editedData.review,
        rating: editedData.rating,
        reviewDate: editedData.reviewDate,
    }


    // console.log(params)
    axiosPrivate
      .post("/api/admin/review/update", params,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        })
      .then((response) => {

        // Find the index of the selectedRow in the sortedData
        const indexOfSelectedRow = rowDataBanner!!.findIndex(
          (row) => row === selectedRowForEdit
        );

        // Create a copy of the sortedData
        const updatedData = [...rowDataBanner!!];
        const data = response.data.links;

        let dataToUpdate: any = {
          ...selectedRowForEdit!!,
          fullname: editedData.fullname,
            review: editedData.review,
            rating: editedData.rating,
            reviewDate: editedData.reviewDate,
        }

        if (!(!data))
          dataToUpdate.imageSrc = data

        updatedData[indexOfSelectedRow] = dataToUpdate
        // changeTableState();
        setRowDataBanner(updatedData);
        showSuccessAlert("Review Successfully Edited");
      })
      .catch((error) => {
        // console.log("error " + error);
        showErrorAlert("Something Went Wrong");
      });
    setEditBannerModalOpen(false);
  };

  const handleDelete = (row: RowDataReview) => {
    setSelectedRowToDelete(row);
    setDeleteConfirmationOpen(true);
  };

  const handleDeleteConfirmed = () => {
    axiosPrivate
      .post("/api/admin/review/delete", {
        id: selectedRowToDelete!!.id,
      })
      .then((response) => {
        setDeleteConfirmationOpen(false);
        if (
          itemsOnPage.current == 1 &&
          currentPage !== 1 &&
          currentPage == Math.ceil(totalRows / itemsPerPage)
        )
          setCurrentPage((prev) => prev - 1);
        else changeTableState();
      })
      .catch((err) => {
        showErrorAlert("An error has occured");
      });

    setDeleteConfirmationOpen(false);
    setSelectedRowToDelete(null);
  };

  const handleDeleteCancelled = () => {
    setDeleteConfirmationOpen(false);
    setSelectedRowToDelete(null);
  };

  const handleView = (row: RowDataReview) => {
    setSelectedRowForView(row);
    setViewBannerModalOpen(true);
  };

  const setSorting = (field: keyof RowDataReview) => {
    const reversed = field === sortBy ? !reverseSortDirection : false;
    setReverseSortDirection(reversed);
    setSortBy(field);
    changeTableState();
  };

  const startIndex = (currentPage - 1) * itemsPerPage;

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handleRowsPerPageChange = (value: number) => {
    setCurrentPage(1);
    setItemsPerPage(value);
  };

  useEffect(() => {
    setRowDataBanner(rowData);
    itemsOnPage.current = fetchedData.itemsOnPage;
    setTotalRows(fetchedData.totalRows);
  }, [rowData]);

  const rowsToMap = rowDataBanner ?? [];
  const rows = rowsToMap.map((row, index) => (
    <Table.Tr key={index}>
      <Table.Td>{startIndex + index + 1}</Table.Td>
      <Table.Td className={classes.titleColumn}>{row.fullname}</Table.Td>
      <Table.Td className={classes.imageLink}>{row.review}</Table.Td>
      <Table.Td className={classes.imageLink}>{row.rating}</Table.Td>
      <Table.Td className={classes.colorAndButtonColumn}>
        <Button
          className={classes.showButton}
          style={{
            backgroundColor: row.show ? "#2BDD66" : "#0014AD",
          }}
          onClick={(e) => toggleVisibility(row, e)}
        >
          {row.show ? "ON" : "OFF"}
        </Button>
      </Table.Td>
      <Table.Td className={classes.actionButtons}>

        <button onClick={() => handleEdit(row)} className={classes.editbutton}>
          <IconEdit /> Edit
        </button>

        <button
          onClick={() => handleDelete(row)}
          className={classes.trashbutton}
        >
          <IconTrash /> Delete
        </button>
      </Table.Td>
    </Table.Tr>
  ));

  const handleSelectPage = (selectedPage: number) => {
    if (selectedPage != currentPage) setCurrentPage(selectedPage);
  };

  return (
    <div className={classes.bannerPageContainer}>
      <ScrollArea className={classes.parentContainer}>
        <Header title="Customer Review" />
        <div className={classes.searchAndAddContainer}>
          <SearchBar
            isLoading={isLoading}
            resetPage={resetPage}
            changeTableState={changeTableState}
            search={search}
            handleSearch={handleSearch}
            setShowFilterOptions={setShowFilterOptions}
            showFilterOptions={showFilterOptions}
            selectedFilters={selectedFilters}
            setSelectedFilters={setSelectedFilters}
            checkboxesFilter={checkboxesFilter}
          />
          <div className={classes.AddButtonContainer}>
            <Button className={classes.addButton} onClick={handleAddBanner}>
              <IconBrandTabler className={classes.iconBrandTabler} />
              Create Review
            </Button>
          </div>
        </div>

        <AddReviewModal
          isOpen={isAddBannerModalOpen}
          onClose={handleAddBannerModalClose}
          onAddBanner={handleAddBannerSuccess}
        />

        <EditReviewModal
          isOpen={isEditBannerModalOpen}
          onClose={() => setEditBannerModalOpen(false)}
          onEditBanner={handleEditBannerSuccess}
          bannerData={selectedRowForEdit || initialData[0]}
        />

        <DeleteModal
          isOpen={isDeleteConfirmationOpen}
          onClose={handleDeleteCancelled}
          onConfirm={handleDeleteConfirmed}
        />

        <CustomTable
          colHeaders={Object.values(ColumnHeaderToFilterPair)}
          filters={Object.values(colFilters)}
          reverseSortDirection={reverseSortDirection}
          sortBy={sortBy}
          setSorting={setSorting}
          customclasses={classes}
          Th={Th}
          rowData={rowDataBanner}
          rows={rows}
        />

        <div className={classes.paginationContainer}>
          <div className={classes.resultCount}>
            <Text>
              {`Showing `}
              <span style={{ fontWeight: "bold" }}>{startIndex + 1}</span>
              {`-`}
              <span style={{ fontWeight: "bold" }}>
                {Math.min(currentPage * itemsPerPage, totalRows)}
              </span>
              {` of `}
              <span style={{ fontWeight: "bold" }}>{totalRows}</span>
              {` results`}
            </Text>
          </div>

          <div className={classes.rightContainer}>
            <div className={classes.rowsPerPageSelector}>
              <Select
                placeholder="Rows per page"
                data={["10", "20", "50"]}
                value={itemsPerPage.toString()}
                onChange={(value) =>
                  handleRowsPerPageChange(parseInt(value as string, 10))
                }
              />
            </div>

            <div className={classes.paginationSelector}>
              <Pagination
                color="blueviolet"
                value={currentPage}
                total={Math.ceil(totalRows / itemsPerPage)}
                onNextPage={() => handlePageChange(currentPage + 1)}
                onPreviousPage={() => handlePageChange(currentPage - 1)}
                onChange={(value: number) => handleSelectPage(value)}
              />
            </div>
          </div>
        </div>
      </ScrollArea>
    </div>
  );
}

export default Review;
